import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { useGlobalWebSocket } from '@/contexts/GlobalWebSocketContext';
import type {
  JobState,
  JobType,
  AIJob,
  AIJobProgress} from './types';
import {
  INITIAL_PROGRESS,
  isAiJobStatusEvent,
  isAiBulkStatusEvent,
  mapProgressPayload,
  makeAbsoluteUrl,
  normalizeImageType,
  type GeneratedImage,
} from './types';

interface AIJobStatusContextType {
  jobs: AIJob[];
  activeJob: AIJob | null;
  queuedJobs: AIJob[];
  completedJobs: AIJob[];
  addJob: (job: Partial<AIJob> & { id: string; type: JobType }) => void;
  removeJob: (jobId: string) => void;
  clearCompletedJobs: () => void;
}

const AIJobStatusContext = createContext<AIJobStatusContextType | undefined>(undefined);

export function useAIJobQueue(): AIJobStatusContextType {
  const context = useContext(AIJobStatusContext);
  if (!context) {
    // Dev-Guard: return safe defaults to prevent HMR crashes
    if (import.meta.env.DEV) {
      return {
        jobs: [],
        activeJob: null,
        queuedJobs: [],
        completedJobs: [],
        addJob: () => {},
        removeJob: () => {},
        clearCompletedJobs: () => {},
      };
    }
    throw new Error('useAIJobQueue must be used within an AIJobStatusProvider');
  }
  return context;
}

interface AIJobStatusProviderProps {
  children: ReactNode;
}

export function AIJobStatusProvider({ children }: AIJobStatusProviderProps): React.ReactElement {
  const [jobs, setJobs] = useState<AIJob[]>([]);
  const { subscribe } = useGlobalWebSocket();

  // Update job state helper
  const updateJobState = useCallback((jobId: string, updates: Partial<AIJob>) => {
    setJobs((prev) =>
      prev.map((job) => (job.id === jobId ? { ...job, ...updates } : job))
    );
  }, []);

  // WebSocket subscription for job status updates
  useEffect(() => {
    const unsubscribe = subscribe((data: unknown) => {
      // Handle ai_job_status events
      if (isAiJobStatusEvent(data)) {
        const { job_id, job_type, state: eventState, progress, generated_content } = data;

        // Find or create job
        setJobs((prev) => {
          const existingJob = prev.find((j) => j.id === job_id);

          // Map state from event to JobState
          let jobState: JobState;
          switch (eventState) {
            case 'queued':
              jobState = 'queued';
              break;
            case 'started':
            case 'progress':
              jobState = 'running';
              break;
            case 'completed':
              jobState = 'completed';
              break;
            case 'error':
              jobState = 'error';
              break;
            default:
              jobState = 'idle';
          }

          // Map progress
          const newProgress: AIJobProgress = progress
            ? mapProgressPayload(progress, existingJob?.progress ?? INITIAL_PROGRESS)
            : (existingJob?.progress ?? INITIAL_PROGRESS);

          // Map generated images
          let generatedImages: GeneratedImage[] = existingJob?.generatedImages ?? [];
          if (generated_content?.images && Array.isArray(generated_content.images)) {
            generatedImages = generated_content.images.map((img) => ({
              id: String(img.id ?? ''),
              type: normalizeImageType(img.type ?? img.category),
              url: makeAbsoluteUrl(String(img.url ?? '')),
              status: 'pending' as const,
              prompt: String(img.prompt ?? ''),
              isAdult: Boolean(img.is_fsk18 ?? img.is_adult),
            }));
          }

          if (existingJob) {
            return prev.map((j) =>
              j.id === job_id
                ? {
                    ...j,
                    state: jobState,
                    progress: newProgress,
                    generatedImages,
                  }
                : j
            );
          } else {
            // Create new job
            const newJob: AIJob = {
              id: job_id,
              type: job_type,
              state: jobState,
              progress: newProgress,
              startedAt: new Date(),
              generatedImages,
              generatedCharacters: [],
            };
            return [...prev, newJob];
          }
        });
      }

      // Handle legacy ai_bulk_status events
      if (isAiBulkStatusEvent(data)) {
        const { job_id, state: eventState, progress } = data;
        if (!job_id) return;

        setJobs((prev) => {
          const existingJob = prev.find((j) => j.id === job_id);

          // Map state
          let jobState: JobState;
          switch (eventState) {
            case 'started':
            case 'progress':
            case 'character_created':
              jobState = 'running';
              break;
            case 'completed':
              jobState = 'completed';
              break;
            case 'error':
              jobState = 'error';
              break;
            case 'stopped':
              jobState = 'stopped';
              break;
            default:
              jobState = 'idle';
          }

          const newProgress: AIJobProgress = progress
            ? mapProgressPayload(progress, existingJob?.progress ?? INITIAL_PROGRESS)
            : (existingJob?.progress ?? INITIAL_PROGRESS);

          if (existingJob) {
            return prev.map((j) =>
              j.id === job_id
                ? { ...j, state: jobState, progress: newProgress }
                : j
            );
          } else {
            const newJob: AIJob = {
              id: job_id,
              type: 'bulk_creation',
              state: jobState,
              progress: newProgress,
              startedAt: new Date(),
              generatedImages: [],
              generatedCharacters: [],
            };
            return [...prev, newJob];
          }
        });
      }
    });

    return () => {
      try {
        unsubscribe();
      } catch { /* noop */ }
    };
  }, [subscribe, updateJobState]);

  const addJob = useCallback((job: Partial<AIJob> & { id: string; type: JobType }) => {
    const newJob: AIJob = {
      id: job.id,
      type: job.type,
      state: job.state ?? 'queued',
      progress: job.progress ?? INITIAL_PROGRESS,
      characterId: job.characterId,
      characterName: job.characterName,
      startedAt: job.startedAt ?? new Date(),
      generatedImages: job.generatedImages ?? [],
      generatedCharacters: job.generatedCharacters ?? [],
    };
    setJobs((prev) => {
      // Avoid duplicates
      if (prev.some((j) => j.id === newJob.id)) {
        return prev.map((j) => (j.id === newJob.id ? { ...j, ...newJob } : j));
      }
      return [...prev, newJob];
    });
  }, []);

  const removeJob = useCallback((jobId: string) => {
    setJobs((prev) => prev.filter((j) => j.id !== jobId));
  }, []);

  const clearCompletedJobs = useCallback(() => {
    setJobs((prev) => prev.filter((j) => j.state !== 'completed' && j.state !== 'error' && j.state !== 'stopped'));
  }, []);

  // Derived state
  const activeJob = useMemo(() => jobs.find((j) => j.state === 'running') ?? null, [jobs]);
  const queuedJobs = useMemo(() => jobs.filter((j) => j.state === 'queued'), [jobs]);
  const completedJobs = useMemo(
    () => jobs.filter((j) => j.state === 'completed' || j.state === 'error' || j.state === 'stopped'),
    [jobs]
  );

  const value = useMemo<AIJobStatusContextType>(
    () => ({
      jobs,
      activeJob,
      queuedJobs,
      completedJobs,
      addJob,
      removeJob,
      clearCompletedJobs,
    }),
    [jobs, activeJob, queuedJobs, completedJobs, addJob, removeJob, clearCompletedJobs]
  );

  return (
    <AIJobStatusContext.Provider value={value}>
      {children}
    </AIJobStatusContext.Provider>
  );
}
